/**
 * Seasonal Calendar Component for v2v2 SWFL Farmers Market Application
 * Interactive monthly calendar showing produce availability
 */



import { useState, useMemo } from 'react';
import { 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  Leaf, 
  Sprout, 
  Clock
} from 'lucide-react';
import { useSeasonal } from '@/hooks/use-seasonal';
import { cn } from '@/lib/utils';
import { Month, AvailabilityStatus } from '@/types/seasonal';

interface SeasonalCalendarProps {
  className?: string;
  variant?: 'grid' | 'list';
  showHighlightedMonthOnly?: boolean;
}

const MONTHS: Month[] = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export function SeasonalCalendar({
  className,
  variant = 'grid',
  showHighlightedMonthOnly = false,
}: SeasonalCalendarProps) {
  const {
    currentMonth,
    getAvailability,
    isLoading,
    getMonthAbbreviation,
    getSeasonLabel,
  } = useSeasonal();

  const [selectedMonth, setSelectedMonth] = useState<Month>(currentMonth);
  const [showDetails, setShowDetails] = useState(false);

  const selectedAvailability = useMemo(() => {
    return getAvailability(selectedMonth);
  }, [getAvailability, selectedMonth]);

  const isCurrentMonth = selectedMonth === currentMonth;
  const season = useMemo(() => {
    const monthIndex = MONTHS.indexOf(selectedMonth);
    if (monthIndex >= 2 && monthIndex <= 4) return 'spring';
    if (monthIndex >= 5 && monthIndex <= 7) return 'summer';
    if (monthIndex >= 8 && monthIndex <= 10) return 'fall';
    return 'winter';
  }, [selectedMonth]);

  const handlePreviousMonth = () => {
    const currentIndex = MONTHS.indexOf(selectedMonth);
    const previousIndex = currentIndex === 0 ? 11 : currentIndex - 1;
    setSelectedMonth(MONTHS[previousIndex]);
    setShowDetails(true);
  };

  const handleNextMonth = () => {
    const currentIndex = MONTHS.indexOf(selectedMonth);
    const nextIndex = currentIndex === 11 ? 0 : currentIndex + 1;
    setSelectedMonth(MONTHS[nextIndex]);
    setShowDetails(true);
  };

  if (isLoading) {
    return (
      <div className={cn('animate-pulse space-y-4', className)}>
        <div className="h-12 bg-muted rounded mb-4" />
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="h-24 bg-muted rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'list') {
    return (
      <div className={cn('space-y-4', className)}>
        <div className="flex items-center justify-between">
          <button
            onClick={handlePreviousMonth}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          
          <div className="text-center">
            <h3 className="text-xl font-semibold flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {selectedMonth}
            </h3>
            <p className="text-sm text-muted-foreground">
              {getSeasonLabel(season as 'spring' | 'summer' | 'fall' | 'winter')} Season
              {isCurrentMonth && ' • Current Month'}
            </p>
          </div>
          
          <button
            onClick={handleNextMonth}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {selectedAvailability && (
          <div className="space-y-4">
            <div className="space-y-2">
              <h4 className="font-medium flex items-center gap-2 text-green-700">
                <Leaf className="h-4 w-4" /> Peak Season
              </h4>
              <div className="flex flex-wrap gap-2">
                {selectedAvailability.peak.map((produce) => (
                  <span
                    key={produce}
                    className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-300"
                  >
                    {produce}
                  </span>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium flex items-center gap-2 text-forest">
                <Sprout className="h-4 w-4" /> Available
              </h4>
              <div className="flex flex-wrap gap-2">
                {selectedAvailability.available.map((produce) => (
                  <span
                    key={produce}
                    className="px-3 py-1 rounded-full text-sm font-medium bg-sage text-bark border border-forest-light"
                  >
                    {produce}
                  </span>
                ))}
              </div>
            </div>

            {selectedAvailability.limited.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium flex items-center gap-2 text-orange-700">
                  <Clock className="h-4 w-4" /> Limited
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedAvailability.limited.map((produce) => (
                    <span
                      key={produce}
                      className="px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800 border border-orange-300"
                    >
                      {produce}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
              <p className="text-sm font-medium">{selectedAvailability.highlights}</p>
              <p className="text-sm text-muted-foreground">
                {selectedAvailability.farmersMarketTips}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {isCurrentMonth && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
          <Leaf className="h-5 w-5 text-green-600" />
          <span className="font-medium text-green-800">
            You are currently viewing {currentMonth} - peak season!
          </span>
        </div>
      )}

      <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
        {MONTHS.map((month) => {
          const availability = getAvailability(month);
          const isSelected = month === selectedMonth;
          const isCurrent = month === currentMonth;

          return (
            <button
              key={month}
              onClick={() => {
                setSelectedMonth(month);
                setShowDetails(true);
              }}
              className={cn(
                'relative p-3 rounded-lg border-2 transition-all text-left',
                isSelected
                  ? 'border-primary bg-primary/5'
                  : 'border-muted hover:border-muted-foreground/30',
                isCurrent && !isSelected && 'ring-2 ring-primary/50'
              )}
            >
              {isCurrent && (
                <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                  Now
                </span>
              )}
              <div className="text-sm font-medium">{getMonthAbbreviation(month)}</div>
              {availability && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {availability.peak.slice(0, 2).map((produce) => (
                    <span
                      key={produce}
                      className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700"
                    >
                      {produce}
                    </span>
                  ))}
                  {availability.peak.length > 2 && (
                    <span className="text-xs text-muted-foreground">
                      +{availability.peak.length - 2}
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {showDetails && selectedAvailability && (
        <div className="p-4 border rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {selectedMonth} Produce
            </h3>
            {isCurrentMonth && (
              <span className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded-full">
                Current Month
              </span>
            )}
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <h4 className="font-medium text-green-700 flex items-center gap-1">
                <Leaf className="h-4 w-4" /> Peak
              </h4>
              <ul className="space-y-1">
                {selectedAvailability.peak.map((item) => (
                  <li key={item} className="text-sm flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium text-forest flex items-center gap-1">
                <Sprout className="h-4 w-4" /> Available
              </h4>
              <ul className="space-y-1">
                {selectedAvailability.available.map((item) => (
                  <li key={item} className="text-sm flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-forest-light" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium text-orange-700 flex items-center gap-1">
                <Clock className="h-4 w-4" /> Limited
              </h4>
              <ul className="space-y-1">
                {selectedAvailability.limited.map((item) => (
                  <li key={item} className="text-sm flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-orange-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm">{selectedAvailability.highlights}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {selectedAvailability.farmersMarketTips}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
